import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import API from "../../../services";
import type { RootState } from "../../../store/store";
import { toast } from "react-toastify";
import { WhitelabelHelpModal } from "./WhitelabelHelpModal";
import {
  type WhitelabelListItemFields,
  formatWlBalance,
  getListStatusBadge,
  getWhitelabelItemStatusRaw,
  getWlPublicUrl,
  getWhitelabelListDisplayName,
  hasUnpaidInProgressWhitelabel,
  isWlPlanPaymentPending,
  normalizeWlStatus,
} from "./whitelabelUi";

type WhitelabelListItem = WhitelabelListItemFields;

const normalizeId = (x: unknown) => String(x ?? "").trim();

const apiGetUserWhitelabels = () => {
  return API.post("/whitelabel/user-whitelabels", {});
};

const apiGetWhitelabelDraft = (payload: { userKey: string; whitelabelId: string }) => {
  return API.post("/whitelabel/draft", payload);
};

const apiGetWhitelabelDetails = (payload: { whitelabelId: string; userKey?: string }) => {
  const body: Record<string, string> = { whitelabelId: payload.whitelabelId };
  if (payload.userKey) body.userKey = payload.userKey;
  return API.post("/whitelabel/get-whitelabel-details", body);
};

const readAxiosErrorMessage = (e: unknown, fallback: string): string => {
  return (
    (e as { response?: { data?: { errorDescription?: string } } })?.response?.data
      ?.errorDescription || fallback
  );
};

/** Matches primary action styling used on whitelabel create flow. */
const BTN_PRIMARY =
  "font-semibold shadow-[0_2px_12px_color-mix(in_srgb,var(--border-color)_25%,transparent)] transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-45 disabled:cursor-not-allowed";

/** Bottom card panel: solid black / near-black. */
const WL_CARD_FOOTER_BG =
  "bg-[linear-gradient(180deg,#0a0a0a_0%,#030303_45%,#000000_100%)]";
const WL_CARD_FOOTER_BORDER = "border-t border-white/[0.08]";

/** Card header only: distinct accents per row. */
const CARD_THEMES = [
  {
    bg: "bg-[linear-gradient(125deg,#2d1f3d_0%,#241a32_45%,#1a1424_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_14%_10%,rgba(192,132,252,0.12)_0%,transparent_72%)]",
    iconRing: "border-purple-400/35 shadow-[inset_0_0_0_1px_rgba(192,132,252,0.12)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#1a2e24_0%,#15261f_48%,#101c18_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_16%_12%,rgba(74,222,128,0.1)_0%,transparent_70%)]",
    iconRing: "border-emerald-400/35 shadow-[inset_0_0_0_1px_rgba(52,211,153,0.12)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#1a2438_0%,#151e30_48%,#101828_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_18%_10%,rgba(96,165,250,0.11)_0%,transparent_72%)]",
    iconRing: "border-sky-400/35 shadow-[inset_0_0_0_1px_rgba(56,189,248,0.12)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#301a22_0%,#28151c_48%,#1c1014_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_12%_12%,rgba(244,114,182,0.09)_0%,transparent_72%)]",
    iconRing: "border-rose-400/30 shadow-[inset_0_0_0_1px_rgba(251,113,133,0.1)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#16302f_0%,#122826_48%,#0e1f1e_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_18%_14%,rgba(45,212,191,0.09)_0%,transparent_72%)]",
    iconRing: "border-teal-400/32 shadow-[inset_0_0_0_1px_rgba(45,212,191,0.1)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#2c2218_0%,#241c14_48%,#1a140f_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_14%_12%,rgba(251,191,36,0.08)_0%,transparent_72%)]",
    iconRing: "border-amber-400/32 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.1)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#281f35_0%,#1f182c_48%,#161020_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_16%_10%,rgba(167,139,250,0.1)_0%,transparent_72%)]",
    iconRing: "border-violet-400/32 shadow-[inset_0_0_0_1px_rgba(167,139,250,0.11)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#151c2e_0%,#121826_48%,#0e1420_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_20%_12%,rgba(129,140,248,0.1)_0%,transparent_72%)]",
    iconRing: "border-indigo-400/32 shadow-[inset_0_0_0_1px_rgba(129,140,248,0.11)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#1e2930_0%,#181f25_48%,#12181d_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_18%_14%,rgba(148,163,184,0.08)_0%,transparent_74%)]",
    iconRing: "border-slate-400/28 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.1)]",
  },
  {
    bg: "bg-[linear-gradient(125deg,#1a2a22_0%,#14221c_48%,#0f1a15_100%)]",
    sheen: "bg-[radial-gradient(ellipse_80%_65%_at_16%_12%,rgba(134,239,172,0.09)_0%,transparent_72%)]",
    iconRing: "border-lime-400/28 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.1)]",
  },
] as const;

const getCardTheme = (index: number) => CARD_THEMES[index % CARD_THEMES.length];

/** First letter or digit from the brand name (uppercased), skipping leading symbols. */
const getBrandInitialLetter = (raw: string) => {
  const s = raw.trim();
  if (!s || s === "—") return "?";
  const match = s.match(/[\p{L}\p{N}]/u);
  return match ? match[0].toLocaleUpperCase(undefined) : "?";
};

/** Up to two characters for draft cards (e.g. camelCase words or first two letters of one word). */
const getBrandMonogram = (raw: string) => {
  const s = raw.trim();
  if (!s || s === "—") return "?";
  const split = s
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const initials: string[] = [];
  for (const t of split) {
    const m = t.match(/[\p{L}\p{N}]/u);
    if (m) initials.push(m[0].toLocaleUpperCase(undefined));
    if (initials.length >= 2) return initials[0] + initials[1];
  }
  if (initials.length === 1 && split.length === 1) {
    const all = split[0].match(/[\p{L}\p{N}]/gu);
    if (all && all.length >= 2) {
      return (all[0] + all[1]).toLocaleUpperCase(undefined);
    }
    return initials[0];
  }
  return initials.join("") || "?";
};

const isWlListItemDraft = (item: WhitelabelListItem) => item.isDraft === true;

const readFirstRow = (row: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const val = row[key];
    if (val !== null && val !== undefined && String(val).trim() !== "") return val;
  }
  return undefined;
};

/** `createdAt` from API in Unix seconds; coerces ms if the value looks like milliseconds. */
const getListItemCreatedAtSeconds = (item: WhitelabelListItem): number | null => {
  const o = item as Record<string, unknown>;
  const sd = o.submittedData;
  const fromSubmitted =
    sd && typeof sd === "object" && !Array.isArray(sd)
      ? readFirstRow(sd as Record<string, unknown>, ["createdAt"])
      : undefined;
  const raw = readFirstRow(o, ["createdAt"]) ?? fromSubmitted;
  if (raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n > 10_000_000_000) return Math.floor(n / 1000);
  return Math.floor(n);
};

/** Second line under points on list cards; `null` if no usable timestamp. */
const formatListItemCreatedAtLine = (createdAtSec: number | null): string | null => {
  if (createdAtSec == null || !Number.isFinite(createdAtSec)) return null;
  const d = new Date(createdAtSec * 1000);
  if (Number.isNaN(d.getTime())) return null;
  return `Created ${d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`;
};

const DRAFT_PROCESS_MS = 10 * 60 * 1000;

function DraftSiteReadyTimer({ createdAtSec }: { createdAtSec: number | null }) {
  const startMs =
    createdAtSec != null && Number.isFinite(createdAtSec) ? createdAtSec * 1000 : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startMs == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  const remainingMs =
    startMs != null
      ? Math.max(0, DRAFT_PROCESS_MS - Math.max(0, now - startMs))
      : 0;
  const showBoxes = startMs != null && remainingMs > 0;
  const totalSec = Math.floor(remainingMs / 1000);
  const mm = Math.min(99, Math.floor(totalSec / 60));
  const ss = totalSec % 60;
  const mmStr = String(mm).padStart(2, "0");
  const ssStr = String(ss).padStart(2, "0");

  const boxClass =
    "flex min-h-[2.05rem] min-w-[2.05rem] items-center justify-center rounded-md border border-emerald-800/50 bg-zinc-950/90 px-1.5 py-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] sm:min-h-[2.15rem] sm:min-w-[2.15rem] sm:px-2 sm:py-1";
  const digitClass =
    "font-mono text-base font-semibold tabular-nums leading-none tracking-tight text-emerald-400 sm:text-lg";
  const unitClass =
    "text-[8px] font-medium uppercase tracking-[0.1em] text-zinc-500";

  const messageClass =
    "text-left text-sm font-medium leading-none text-zinc-300 sm:text-base";
  /** Matches digit row so MIN / SEC sit under the boxes and colon. */
  const timerGridClass =
    "grid shrink-0 grid-cols-[2.05rem_0.4rem_2.05rem] items-center justify-items-center gap-x-0.5 sm:grid-cols-[2.15rem_0.45rem_2.15rem] sm:gap-x-1";

  if (!showBoxes) {
    return (
      <div className="flex w-full items-center py-2">
        <p className={`${messageClass} leading-snug`}>Your website will be ready soon.</p>
      </div>
    );
  }

  return (
    <div className="w-full pt-0.5" aria-live="polite">
      <div className="flex w-full flex-nowrap items-center justify-between gap-2 sm:gap-3">
        <p className={`min-w-0 shrink whitespace-nowrap ${messageClass}`}>
          Your website will be ready within
        </p>
        <div className={timerGridClass}>
          <div className={`${boxClass} w-full justify-self-stretch`}>
            <span className={digitClass}>{mmStr}</span>
          </div>
          <span
            className="select-none text-sm font-light leading-none text-zinc-500 sm:text-base"
            aria-hidden
          >
            :
          </span>
          <div className={`${boxClass} w-full justify-self-stretch`}>
            <span className={digitClass}>{ssStr}</span>
          </div>
        </div>
      </div>
      <div className="mt-1 flex w-full justify-end">
        <div className={timerGridClass}>
          <span className={unitClass}>min</span>
          <span className="block w-full min-w-[0.4rem] sm:min-w-[0.45rem]" aria-hidden />
          <span className={unitClass}>sec</span>
        </div>
      </div>
    </div>
  );
}

/** Strip `http(s)://` and trailing slashes for list domain line (API `webUrl`). */
const stripUrlSchemeForDisplay = (raw: string) =>
  String(raw ?? "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");

const getCardDomainLabel = (item: WhitelabelListItem) => {
  const o = item as Record<string, unknown>;
  const web = String(o.webUrl ?? "").trim();
  if (web) return stripUrlSchemeForDisplay(web);

  const explicit = getWlPublicUrl(item);
  if (explicit) {
    try {
      return new URL(explicit).hostname;
    } catch {
      return explicit.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
    }
  }
  const subdomain = String(item.subdomain ?? "").trim().toLowerCase();
  return subdomain ? `${subdomain}.whitelabel.com` : "Domain pending";
};

const getStatusPillStyle = (label: string) => {
  const lower = label.toLowerCase();
  if (lower.includes("live")) {
    return {
      pill: "border-emerald-800/40 bg-emerald-950/55",
      dot: "bg-emerald-400",
      text: "text-emerald-300",
    };
  }
  if (lower.includes("failed")) {
    return {
      pill: "border-rose-900/40 bg-rose-950/50",
      dot: "bg-rose-400",
      text: "text-rose-200",
    };
  }
  if (lower.includes("processing")) {
    return {
      pill: "border-amber-900/35 bg-[color-mix(in_srgb,#422006_88%,#1c1410)]",
      dot: "bg-amber-400",
      text: "text-amber-200",
    };
  }
  if (lower.includes("initiated")) {
    return {
      pill: "border-slate-600/35 bg-slate-900/65",
      dot: "bg-violet-400",
      text: "text-violet-200",
    };
  }
  if (lower.includes("awaiting") || lower.includes("payment")) {
    return {
      pill: "border-amber-900/35 bg-amber-950/45",
      dot: "bg-amber-300",
      text: "text-amber-100",
    };
  }
  return {
    pill: "border-zinc-600/35 bg-zinc-900/60",
    dot: "bg-sky-300",
    text: "text-sky-200",
  };
};

const getPointsDisplay = (item: WhitelabelListItem) => {
  const st = normalizeWlStatus(getWhitelabelItemStatusRaw(item));
  if (st === "INITIATED") return { kind: "dash" as const };
  const formatted = formatWlBalance(item, { integerDisplay: true });
  return { kind: "number" as const, value: formatted };
};

const setupProgressForIndex = (idx: number) => {
  const steps = [0.38, 0.55, 0.72, 0.44, 0.63];
  return steps[idx % steps.length];
};

const capitalizeFirstLetter = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return value;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const Whitelabels = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = useSelector((state: RootState) => state.userDetails.userDetails);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<WhitelabelListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showOfflineThanks, setShowOfflineThanks] = useState(false);
  const [openingWhitelabelId, setOpeningWhitelabelId] = useState<string | null>(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const hideCreateForUnpaidDraft = hasUnpaidInProgressWhitelabel(items);
  const hasDraftInProgress = items.some(isWlListItemDraft);
  const disableCreateButton = hideCreateForUnpaidDraft || hasDraftInProgress;

  const loadWhitelabelList = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const response = await apiGetUserWhitelabels();
      const data = response?.data;
      if (data?.status !== "RS_OK") {
        if (!mountedRef.current) return;
        const msg = data?.errorDescription || "Error loading whitelabels";
        setItems([]);
        setError(msg);
        toast.error(msg);
        return;
      }

      const raw = data?.whitelabels ?? [];
      const list: WhitelabelListItem[] = Array.isArray(raw) ? raw : [];
      if (mountedRef.current) setItems(list);
    } catch (e: unknown) {
      if (!mountedRef.current) return;
      const msg = readAxiosErrorMessage(e, "Error loading whitelabels");
      setItems([]);
      setError(msg);
      toast.error(msg);
    } finally {
      if (!quiet && mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const st = location.state as { showOfflinePaymentThanks?: boolean } | null | undefined;
    if (!st?.showOfflinePaymentThanks) return;
    setShowOfflineThanks(true);
    navigate("/whitelabel", { replace: true, state: {} });
  }, [location.state, navigate]);

  useEffect(() => {
    if (!showOfflineThanks) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowOfflineThanks(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showOfflineThanks]);

  useEffect(() => {
    void loadWhitelabelList(false);
  }, [loadWhitelabelList]);

  const goToCreate = () => {
    if (disableCreateButton) return;
    try {
      navigate("/whitelabel/create");
    } catch {
      toast.error("Unable to open create whitelabel.");
    }
  };

  const goToWhitelabelDetails = async (item: WhitelabelListItem) => {
    const whitelabelId = normalizeId(item.whitelabelId);
    const packageId = normalizeId(item.packageId);
    if (!whitelabelId) return;

    const isDraftCard = isWlListItemDraft(item);
    const failMsg = isDraftCard
      ? "Failed to load draft details."
      : "Failed to load whitelabel details.";

    setOpeningWhitelabelId(whitelabelId);
    try {
      let response: { data?: { status?: string; errorDescription?: string } };
      if (!isDraftCard) {
        response = await apiGetWhitelabelDetails({ whitelabelId });
      } else {
        const userKey = String(userDetails?.UserKey || "").trim();
        if (!userKey) {
          toast.error("Whitelabel details are unavailable.");
          await loadWhitelabelList(true);
          return;
        }
        response = await apiGetWhitelabelDraft({ userKey, whitelabelId });
      }

      const data = response?.data;
      if (data?.status !== "RS_OK") {
        const msg = data?.errorDescription || failMsg;
        toast.error(msg);
        await loadWhitelabelList(true);
        return;
      }

      if (!mountedRef.current) return;

      const qs = new URLSearchParams();
      if (packageId) qs.set("packageId", packageId);
      qs.set("mode", isDraftCard ? "pending" : "active");
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const raw = item as Record<string, unknown>;
      const paymentPending = raw.paymentPending === true;
      navigate(`/whitelabel/details/${encodeURIComponent(whitelabelId)}${suffix}`, {
        state: {
          paymentPending,
          listPrefetch: {
            kind: isDraftCard ? ("pending" as const) : ("active" as const),
            data,
          },
        },
      });
    } catch (e: unknown) {
      toast.error(readAxiosErrorMessage(e, failMsg));
      await loadWhitelabelList(true);
    } finally {
      if (mountedRef.current) setOpeningWhitelabelId(null);
    }
  };

  const draftItems = items.filter(isWlListItemDraft);
  const publishedItems = items.filter((it) => !isWlListItemDraft(it));
  type WlDisplayRow =
    | { kind: "card"; item: WhitelabelListItem; themeIndex: number }
    | { kind: "sep" };
  const displayRows: WlDisplayRow[] = [];
  for (let i = 0; i < draftItems.length; i += 1) {
    displayRows.push({ kind: "card", item: draftItems[i], themeIndex: i });
  }
  if (draftItems.length > 0 && publishedItems.length > 0) {
    displayRows.push({ kind: "sep" });
  }
  const draftCount = draftItems.length;
  for (let i = 0; i < publishedItems.length; i += 1) {
    /* Offset so first published card does not reuse getCardTheme(0) after draft(s). */
    displayRows.push({ kind: "card", item: publishedItems[i], themeIndex: i + draftCount });
  }

  return (
    <div className="text-white px-3 sm:px-4 py-[5rem] pb-[8rem]">
      {showOfflineThanks && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="presentation"
          onClick={() => setShowOfflineThanks(false)}
        >
          <div
            className="relative w-full max-w-[240px] min-h-[150px] overflow-hidden rounded-2xl border-2 border-[color-mix(in_srgb,var(--border-color)_28%,#3f4f52)] bg-zinc-950 px-4 pb-5 pt-6 shadow-[0_10px_32px_rgba(0,0,0,0.7)] ring-1 ring-inset ring-white/[0.06]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="offline-payment-thanks-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[color-mix(in_srgb,var(--border-color)_14%,#1e293b)] via-zinc-900 to-zinc-950"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_125%_85%_at_50%_-35%,color-mix(in_srgb,var(--border-color)_42%,transparent)_0%,transparent_58%)] opacity-90"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent"
              aria-hidden
            />
            <button
              type="button"
              aria-label="Close"
              className="absolute right-1.5 top-1.5 z-20 flex h-8 w-8 items-center justify-center rounded-md text-xl font-light leading-none text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-300"
              onClick={() => setShowOfflineThanks(false)}
            >
              ×
            </button>
            <div className="relative z-10 flex min-h-0 flex-col items-center px-1 pt-1 text-center">
              <p
                id="offline-payment-thanks-title"
                className="text-base leading-relaxed text-zinc-300 sm:text-[1.05rem]"
              >
                Thank you!
                <br />
                Our team will reach out to you soon.
              </p>
              <button
                type="button"
                className={`mt-4 shrink-0 px-4 py-1.5 text-xs text-white rounded-md border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] ${BTN_PRIMARY}`}
                onClick={() => setShowOfflineThanks(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-4xl">
        <header className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="bg-inputbox-color flex h-8 w-8 shrink-0 items-center justify-center rounded"
            aria-label="Go back"
            title="Back"
          >
            <ArrowBackIcon
              className="text-white"
              style={{ width: "20px", height: "20px", color: "#FFFFFF" }}
            />
          </button>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <h1 className="min-w-0 text-xl font-semibold leading-tight text-zinc-100 sm:text-2xl">
              My brands
            </h1>
            {!loading && !error ? (
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={goToCreate}
                  disabled={disableCreateButton}
                  aria-disabled={disableCreateButton}
                  className={`rounded-md bg-[var(--border-color)] px-3 py-1.5 text-sm font-semibold text-white shadow-sm ${BTN_PRIMARY}`}
                >
                  New
                </button>
              </div>
            ) : null}
          </div>
        </header>
        {loading ? (
          <p className="mt-8 text-center text-sm text-zinc-400">Loading...</p>
        ) : null}

        {!loading && !!error ? (
          <div className="mt-6 flex min-h-[45vh] flex-col items-center justify-center px-2">
            <p className="text-center text-sm text-amber-400 sm:text-base">
              Error loading whitelabels
            </p>
          </div>
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <div className="mt-6 flex min-h-[45vh] flex-col items-center justify-center px-2">
            <p className="text-center text-sm text-zinc-400 sm:text-base">
              No whitelabels yet
            </p>
          </div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="mt-6 space-y-3.5">
            {displayRows.map((row, listIdx) => {
              if (row.kind === "sep") {
                return (
                  <div
                    key="wl-draft-published-divider"
                    className="flex items-center gap-3 py-1.5"
                    role="separator"
                    aria-orientation="horizontal"
                  >
                    <div
                      className="h-[2px] min-h-[2px] flex-1 rounded-full bg-gradient-to-r from-transparent to-[color-mix(in_srgb,var(--border-color)_82%,#f8fafc)] shadow-[0_0_14px_1px_color-mix(in_srgb,var(--border-color)_42%,transparent)]"
                      aria-hidden
                    />
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color-mix(in_srgb,var(--border-color)_22%,#fafafa)]">
                      Live brands
                    </span>
                    <div
                      className="h-[2px] min-h-[2px] flex-1 rounded-full bg-gradient-to-l from-transparent to-[color-mix(in_srgb,var(--border-color)_82%,#f8fafc)] shadow-[0_0_14px_1px_color-mix(in_srgb,var(--border-color)_42%,transparent)]"
                      aria-hidden
                    />
                  </div>
                );
              }
              const it = row.item;
              const themeIndex = row.themeIndex;
              const id = normalizeId(it.whitelabelId);
              const nameKey = getWhitelabelListDisplayName(it);
              const displayName = nameKey || id || "—";
              const isDraft = isWlListItemDraft(it);
              const raw = it as Record<string, unknown>;
              const paymentPendingFlag = raw.paymentPending === true;
              const showPay =
                isWlPlanPaymentPending(it) || (isDraft && paymentPendingFlag);
              const badge = getListStatusBadge(it);
              const statusPill = getStatusPillStyle(badge.label);
              const cardTitle = capitalizeFirstLetter(displayName);
              const domainLabel = getCardDomainLabel(it);
              const cardTheme = getCardTheme(themeIndex);
              const brandMark = isDraft ? getBrandMonogram(displayName) : getBrandInitialLetter(displayName);
              const packageLabel = isDraft
                ? String((it as Record<string, unknown>).packageName ?? "").trim() || "—"
                : "";
              const createdAtSec = getListItemCreatedAtSeconds(it);
              const createdAtLine = formatListItemCreatedAtLine(createdAtSec);
              const pointsDisplay = getPointsDisplay(it);
              const st = normalizeWlStatus(getWhitelabelItemStatusRaw(it));
              const showSetupBar =
                !isDraft &&
                st === "IN_PROGRESS" &&
                !showPay &&
                badge.label.toLowerCase().includes("processing");
              const setupPct = setupProgressForIndex(themeIndex);
              const isOpeningDetails = openingWhitelabelId !== null;
              const isThisOpening = Boolean(id && openingWhitelabelId === id);
              const openDetails = () => {
                if (!id || isOpeningDetails) return;
                void goToWhitelabelDetails(it);
              };
              return (
                <div
                  key={`${id || nameKey || "wl"}-${listIdx}`}
                  role={id ? "button" : undefined}
                  tabIndex={id && !isOpeningDetails ? 0 : undefined}
                  aria-busy={isThisOpening ? true : undefined}
                  aria-disabled={id && isOpeningDetails ? true : undefined}
                  aria-label={id ? `Open details for ${displayName}` : undefined}
                  onClick={id && !isOpeningDetails ? openDetails : undefined}
                  onKeyDown={
                    id && !isOpeningDetails
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDetails();
                          }
                        }
                      : undefined
                  }
                  className={`overflow-hidden rounded-2xl border border-zinc-400/55 bg-zinc-950/20 shadow-[0_12px_28px_-18px_rgba(0,0,0,0.75),inset_0_0_0_1px_rgba(255,255,255,0.08)] ring-1 ring-inset ring-white/[0.1] ${
                    id
                      ? isOpeningDetails
                        ? "cursor-wait opacity-60"
                        : "cursor-pointer transition-[opacity,transform] hover:opacity-[0.97] hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--border-color)_45%,white)]/35"
                      : ""
                  }`}
                >
                  <div className="relative isolate min-h-[5.25rem] overflow-hidden px-3.5 pb-3 pt-3.5 sm:px-4 sm:pb-3.5 sm:pt-4">
                    <div className={`pointer-events-none absolute inset-0 ${cardTheme.bg}`} aria-hidden />
                    <div className={`pointer-events-none absolute inset-0 ${cardTheme.sheen}`} aria-hidden />
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] via-transparent to-black/25"
                      aria-hidden
                    />
                    <div className="relative z-10 flex items-start gap-3">
                      <span
                        className={`mt-0.5 inline-flex h-10 shrink-0 select-none items-center justify-center rounded-xl border bg-black/25 font-semibold leading-none text-white backdrop-blur-[2px] ${cardTheme.iconRing} ${
                          isDraft ? "min-w-[2.75rem] px-1.5 text-sm sm:text-base" : "w-10 text-lg sm:text-xl"
                        }`}
                        aria-hidden
                      >
                        {brandMark}
                      </span>
                      <div className="min-w-0 flex-1 pr-2 pt-0.5">
                        <p className="truncate text-lg font-semibold leading-tight tracking-tight text-white sm:text-xl">
                          {cardTitle}
                        </p>
                        <p className="mt-1 truncate text-[0.8125rem] text-white/55 sm:text-sm">
                          {domainLabel}
                        </p>
                      </div>
                      {!isDraft ? (
                        <div
                          className={`flex shrink-0 items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusPill.pill}`}
                        >
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusPill.dot}`} />
                          <span className={statusPill.text}>{badge.label}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div
                    className={`px-3.5 py-3.5 sm:px-4 ${WL_CARD_FOOTER_BG} ${WL_CARD_FOOTER_BORDER}`}
                  >
                    {isDraft ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-0 divide-x divide-white/[0.12]">
                          <div className="min-w-0 pr-3">
                            <p className="text-[11px] font-normal uppercase tracking-[0.05em] text-zinc-500">
                              Package
                            </p>
                            <p className="mt-1 truncate text-base font-semibold leading-tight text-zinc-100 sm:text-lg">
                              {packageLabel}
                            </p>
                          </div>
                          <div className="min-w-0 pl-3">
                            <p className="text-[11px] font-normal uppercase tracking-[0.05em] text-zinc-500">
                              Status
                            </p>
                            <p
                              className={`mt-1 text-base font-semibold leading-tight sm:text-lg ${badge.className}`}
                            >
                              {badge.label}
                            </p>
                          </div>
                        </div>
                        {paymentPendingFlag ? (
                          <div className="flex w-full items-center py-2">
                            <p className="text-left text-sm font-medium leading-snug text-zinc-300 sm:text-base">
                              Complete your payment.
                            </p>
                          </div>
                        ) : (
                          <DraftSiteReadyTimer createdAtSec={createdAtSec} />
                        )}
                      </div>
                    ) : (
                      <div className="flex items-end justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-lg font-light tabular-nums leading-tight text-zinc-100 sm:text-xl">
                            {pointsDisplay.kind === "dash"
                              ? "—"
                              : `${pointsDisplay.value} points`}
                          </p>
                          {createdAtLine ? (
                            <p className="mt-1 truncate text-[0.8125rem] font-normal leading-snug text-zinc-500 sm:text-sm">
                              {createdAtLine}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          {showSetupBar ? (
                            <div className="w-[min(7.5rem,28vw)]">
                              <p className="mb-1 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                                Setup
                              </p>
                              <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.12]">
                                <div
                                  className="h-full rounded-full bg-amber-500"
                                  style={{ width: `${Math.round(setupPct * 100)}%` }}
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <WhitelabelHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        onSelectCreateWhitelabelTour={() => {
          navigate("/whitelabel/create/tour?from=help");
          setHelpModalOpen(false);
        }}
        onSelectPaymentHelpTour={(method) => {
          navigate(`/whitelabel/help/payment-tour?from=help&method=${method}`);
          setHelpModalOpen(false);
        }}
        onSelectAddFundsTour={() => {
          navigate("/whitelabel/help/add-funds-tour?from=help");
          setHelpModalOpen(false);
        }}
      />
      <button
        type="button"
        className={`fixed bottom-24 right-4 z-40 flex items-center gap-2 rounded-full border-2 border-[color-mix(in_srgb,var(--border-color)_48%,#334155)] bg-[color-mix(in_srgb,var(--border-color)_24%,#0c1222)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_24px_rgba(0,0,0,0.35)] transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 ${BTN_PRIMARY}`}
        onClick={() => setHelpModalOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={helpModalOpen}
      >
        Help
      </button>
    </div>
  );
};

export default Whitelabels;
